/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { theme } from "styles/theme";
import { useEffect, useState } from "react";
import { TDevice } from "apis/visitors/device";
import UI from "@mile-meeting/mile-ui";
import Button from "uis/Button";
import {
  deleteDoorBellReceiver,
  getDoorBellInfo,
  registerDoorBellChannel,
  registerDoorBellReceiver,
  updateDoorBellActive,
} from "apis/visitors/setting/doorbell";
import AlertModal from "uis/modal/alert-modal";
import ChannelItem from "uis/layout/channel-item";
import { Alert, Snackbar } from "@mui/material";
import { AddChannelModal } from "uis/modal/add-channel-modal";
import HostProfile from "uis/layout/manager-item";
import { AddManagerModal } from "uis/modal/add-manager-modal";
import {
  THost,
  TRegisterMessengerChannel,
  TMessengerChannel,
  TRegisterMessengerHost,
} from "apis/visitors/setting/type";
import { TMember } from "types/user.types";
import { getMessengers, TSlackChannel } from "apis/messangers";

const Title = styled.p`
  color: var(--alias-color-label-normal);
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
`;
const GuideContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  line-height: 22px;
`;
const Guide = styled.p`
  font-weight: 500;
  font-size: 15px;
  color: var(--alias-color-label-alternative);
  line-height: 22px;
  vertical-align: middle;
`;
const GuideLink = styled.a`
  color: var(--alias-color-primary-normal);
  font-weight: 700;
  font-size: 14px;
  line-height: 22px;
  vertical-align: bottom;
`;

const ActiveContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-content: center;
  height: 84px;
  align-items: center;
  padding: 0 40px;
  background-color: ${theme.neutral50};
`;

const EditContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 20px;
  align-items: center;
`;

const SectionTitle = styled.p`
  color: ${theme.neutral900};
  font-size: 16px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Annotation = styled.p`
  color: ${theme.neutral500};
  font-size: 16px;
  font-weight: 500;
`;

const TableHeader = styled.div`
  border-radius: 12px;
  background: ${theme.neutral50};
  padding: 12px 24px;
  align-items: center;
  display: flex;
  gap: 20px;
`;

const TableHeaderLabel = styled.p`
  font-size: 14px;
  font-weight: 500;
  width: 160px;
`;

const DoorBell = ({ device, onChange }: { device: TDevice; onChange?: () => void }) => {
  const [isOpenManagerModal, setIsOpenManagerModal] = useState<boolean>(false);
  const [isOpenChannelModal, setIsOpenChannelModal] = useState<boolean>(false);
  const [receivers, setReceivers] = useState<TRegisterMessengerHost[] | undefined>();
  const [channels, setChannels] = useState<TRegisterMessengerChannel[] | undefined>();
  const [isOpenAlertModal, setIsOpenAlertModal] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [usedSlack, setUsedSlack] = useState<boolean>(false);

  const onHandleDeleteHost = async (id: string) => {
    const res = await deleteDoorBellReceiver(id);
    if (res) {
      onChangeDoorBellInfo();
    }
  };

  const onChangeDoorBellInfo = async () => {
    const res = await getDoorBellInfo(device.id);
    if (res) {
      setSuccess(true);
      setReceivers(res.receivers);
      setChannels(res.channels);
    }
  };

  const onRegisterMessengerChannel = async (channels: TSlackChannel) => {
    const res = await registerDoorBellChannel(device.id, "SLACK", channels.slackChannelId, channels.slackChannelName);
    if (res) {
      onChangeDoorBellInfo();
    }
  };

  const onRegisterMessengerReceivers = async (members: TMember[]) => {
    const res = await registerDoorBellReceiver(device.id, members);
    if (res.success) {
      onChangeDoorBellInfo();
    }
  };

  useEffect(() => {
    getDoorBellInfo(device.id).then((res) => {
      if (res) {
        setReceivers(res.receivers);
        setChannels(res.channels);
      }
    });

    getMessengers().then((res) => {
      if (res.data?.some((item) => item.messengerProvider === "SLACK")) {
        setUsedSlack(true);
      }
    });
  }, []);

  return (
    <div
      css={css`
        height: 80%;
        overflow: auto;
      `}
    >
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success">설정이 업데이트 되었습니다.</Alert>
      </Snackbar>
      <ActiveContainer>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <Title>초인종 기능 활성화</Title>
        </div>
        <UI.Selection.Toggle.Default
          size="large"
          state={device.isDoorBellEnabled}
          onChange={() => {
            setIsOpenAlertModal(true);
          }}
        ></UI.Selection.Toggle.Default>
      </ActiveContainer>
      {device.isDoorBellEnabled && (
        <>
          <div
            css={css`
              padding: 60px 30px 50px 30px;
            `}
          >
            <EditContainer>
              <SectionTitle>
                초인종 알림 채널
                <br />
                <Annotation>방문자가 초인종 기능으로 호출하면 연동된 메신저 채널로 알림이 전송됩니다.</Annotation>
              </SectionTitle>

              <UI.Button
                onClick={() => {
                  if (usedSlack) setIsOpenChannelModal(true);
                }}
                title="채널 추가하기"
                size="large"
                disabled={!usedSlack}
              />
            </EditContainer>
            <TableHeader>
              <TableHeaderLabel>채널 이름</TableHeaderLabel>
            </TableHeader>
            {channels && channels.length > 0 ? (
              channels.map((item, index) => {
                return (
                  <ChannelItem
                    key={index}
                    channel={item}
                    isMessengerConnected={item.isActive}
                    onDelete={() => {
                      if (item.id) onHandleDeleteHost(item.id);
                    }}
                  />
                );
              })
            ) : (
              <p
                css={css`
                  color: ${theme.neutral500};
                  font-weight: 400;
                  font-size: 16px;
                  padding: 24px;
                `}
              >
                등록된 채널이 없습니다
              </p>
            )}
          </div>
          <div
            css={css`
              padding: 0 30px;
            `}
          >
            <EditContainer>
              <SectionTitle>
                초인종 알림 담당자
                <br />
                <Annotation>
                  방문자가 초인종 기능으로 호출하면 해당 담당자의 메신저 DM으로 알람이 전송됩니다.
                </Annotation>
              </SectionTitle>

              <Button
                onClick={() => setIsOpenManagerModal(true)}
                title="담당자 추가하기"
                styles={{
                  container: css`
                    padding: 14px 20px;
                    width: max-content;
                  `,
                }}
              />
            </EditContainer>
            <TableHeader>
              <TableHeaderLabel>이름</TableHeaderLabel>
              <TableHeaderLabel>이메일</TableHeaderLabel>
            </TableHeader>
            {receivers && receivers.length > 0 ? (
              receivers.map((item, index) => {
                return (
                  <HostProfile
                    image={item.host.image ?? ""}
                    name={item.host.nickname}
                    email={item.host.email}
                    key={index}
                    onDelete={() => {
                      onHandleDeleteHost(item.id);
                    }}
                  />
                );
              })
            ) : (
              <p
                css={css`
                  color: ${theme.neutral500};
                  font-weight: 400;
                  font-size: 16px;
                  padding: 24px;
                `}
              >
                등록된 담당자가 없습니다
              </p>
            )}
          </div>
        </>
      )}
      <AddManagerModal
        onUpdate={async (value: TMember[]) => {
          if (value.length > 0) {
            onRegisterMessengerReceivers(value);
            setIsOpenManagerModal(false);
          }
        }}
        registeredManagers={receivers ? receivers.map((item) => item.host) : []}
        modalIsOpen={isOpenManagerModal}
        closeModal={() => setIsOpenManagerModal(false)}
      />
      <AddChannelModal
        registered={
          channels
            ? channels?.map((item) => {
                return {
                  messengerType: item.messengerType,
                  channelId: item.channelId,
                  channelName: item.channelName,
                  isActive: item.isActive,
                };
              })
            : []
        }
        onUpdate={async (value: TSlackChannel) => {
          onRegisterMessengerChannel(value);
        }}
        modalIsOpen={isOpenChannelModal}
        closeModal={() => setIsOpenChannelModal(false)}
      />
      <AlertModal
        title="초인종 기능을 변경하시겠습니까?"
        btnTitle="변경하기"
        content={
          <p
            css={css`
              text-align: center;
              color: ${theme.neutral500};
            `}
          >
            초인종 활성화 여부를 변경하면,
            <br />
            Mile Visitors 태블릿의 첫 화면이 변경됩니다.
          </p>
        }
        modalIsOpen={isOpenAlertModal}
        closeModal={() => setIsOpenAlertModal(false)}
        onChange={async () => {
          const res = await updateDoorBellActive(device.id, !device.isDoorBellEnabled);
          if (res) {
            onChange?.();
            setIsOpenAlertModal(false);
          } else {
            alert("잠시후 다시 시도해주세요.");
          }
        }}
      />
    </div>
  );
};

export default DoorBell;
